// What one player may see of a game: the full state with hidden information taken out.
//   Public: the card row, everyone's board and civil hand (civil cards are taken openly), wonders, tactics in play,
//           revealed events, and the contents (not the order) of every civil deck.
//   Hidden: other players' military hands, the military decks and discard, the current events, and the future events
//           the viewer did not prepare. These become face-down placeholders that keep only their age (card backs show
//           it) and get fresh ids, since real ids would give away the order cards were dealt in.
//   Never sent: the random seed, which would predict every shuffle still to come.
//   Once the game is over: everyone's military hand, the current and future events, and who prepared which, face up.
//   A mix of leaders and wonders (Constants/CardVersions.js): those picked that are not revealed yet (all of them, in
//           a secret mix; the later ages', in one revealed age by age) are face down in the civil decks, and the
//           setup's selection has only those already drawn.
// The viewer also gets the cards that could be in the military deck: the age's military cards less those they know
// are somewhere else (see Authority.KnowledgeFor).
const HiddenType = 'Hidden';

const clone = (data) => JSON.parse(JSON.stringify(data));
const AgeIndex = (age) => ['Age A', 'Age I', 'Age II', 'Age III', 'Age IV'].indexOf(age);

// viewer: the player number; knowledge: { seen: Set of card ids the viewer knows, candidates: [{ type, name, age, count }] }
const Redact = (gameStateData, viewer, knowledge) => {
    const data = clone(gameStateData);
    let nextId = -1;
    const facedown = (card) => card && ({ type: HiddenType, name: 'Face-down card', age: card.age, id: nextId-- });
    // Sorted first, then numbered, so neither the order nor the ids say anything about how the cards were dealt
    const unordered = (cards) => cards
        // (a changed card's version is the setup's, no secret)
        .map(card => ({ type: card.type, name: card.name, age: card.age, ...(card.version ? { version: card.version } : {}),
            ...(card.of ? { of: card.of } : {}) }))
        .sort((a, b) => AgeIndex(a.age) - AgeIndex(b.age) || a.type.localeCompare(b.type) || a.name.localeCompare(b.name))
        .map(card => ({ ...card, id: nextId-- }));

    data.rngSeed = null;
    // (a finished game keeps no secrets of the events and hands)
    const over = !!data.finished;
    // Who prepared which face-down event is public; what it is, only to whoever prepared it (to everyone once the game
    // is over). The owners by real id are not sent (real ids could be matched against the deal order).
    // Sorted, so the list says nothing about where in the deck each one is
    const prepared = (cards) => cards
        .filter(card => data.eventOwners?.[card.id] !== undefined)
        .map(card => ({ player: data.eventOwners[card.id], age: card.age, ...(over || knowledge.seen.has(card.id) ? { type: card.type, name: card.name } : {}) }))
        .sort((a, b) => a.player - b.player || AgeIndex(a.age) - AgeIndex(b.age) || (a.name ?? '').localeCompare(b.name ?? ''));
    data.eventsPrepared = { current: prepared(data.currentEvents), future: prepared(data.futureEvents) };
    delete data.eventOwners;
    data.playerStateData.forEach((player, index) => {
        if (index !== viewer && !over)
        {
            // Face down, except cards everyone knows are there (a declined pact back in the offerer's hand)
            player.militaryHand = player.militaryHand.map(card => knowledge.seen.has(card?.id) ? card : facedown(card));
        }
    });
    data.militaryDeck = data.militaryDeck.map(facedown);
    data.militaryDiscard = (data.militaryDiscard ?? []).map(facedown);
    // Face down, except the top one when the viewer has looked at it (Joan of Arc)
    data.currentEvents = data.currentEvents.map((card, index) => over || (index === 0 && knowledge.peeked?.has(card.id)) ? card : facedown(card));
    // The box is out of the game: no one sees it
    data.eventBox = [];
    data.futureEvents = data.futureEvents.map(card => over || knowledge.seen.has(card.id) ? card : facedown(card));
    // A mix's leaders and wonders not revealed yet: face down, as an unknown leader or wonder (of: which; how many of each
    // an age has is no secret), and not in the selection
    const reveal = data.setup?.cards === 'mix' ? data.setup.reveal : null;
    const revealed = (age) => reveal === null || reveal === 'public' || (reveal === 'byAge' && AgeIndex(age) <= AgeIndex(data.age));
    const unrevealed = (cards, age) => revealed(age) ? cards
        : cards.map(card => card && ['Leader', 'Wonder'].includes(card.type)
            ? { type: HiddenType, name: `Unknown ${card.type.toLowerCase()}`, age: card.age, of: card.type } : card);
    if (data.setup?.selection)
    {
        // (an age not revealed: only those already drawn, which everyone has seen in the card row, and how many there
        // are, which is no secret)
        const inDecks = new Set([...data.civilDeck, ...(data.upcomingDecks ?? []).flatMap(deck => deck.civil)].map(card => card?.name));
        data.setup.selection = Object.fromEntries(Object.entries(data.setup.selection).map(([age, picked]) => [age, revealed(age) ? picked : {
            leaders: picked.leaders.filter(name => !inDecks.has(name)),
            wonders: picked.wonders.filter(name => !inDecks.has(name)),
            partial: true,
            count: { leaders: picked.leaders.length, wonders: picked.wonders.length },
        }]));
    }
    data.civilDeck = unordered(unrevealed(data.civilDeck, data.age));
    data.upcomingDecks = (data.upcomingDecks ?? []).map(deck => ({
        age: deck.age,
        civil: unordered(unrevealed(deck.civil, deck.age)),
        military: deck.military.map(facedown),
    }));
    // Other players' open decisions: only who, on what, and whether the turn waits for them
    data.decisions = (data.decisions ?? []).map(decision => decision.player === viewer ? decision
        : { id: decision.id, player: decision.player, event: decision.event, blocking: decision.blocking, options: [],
            ...(decision.order !== undefined ? { order: decision.order } : {}) });
    data.viewer = viewer;
    data.militaryDeckCandidates = knowledge.candidates;
    return data;
};

module.exports = {
    Redact,
    HiddenType,
};
